/**
 * Which market this build is serving, answered SYNCHRONOUSLY at module load.
 *
 * The CRM already knew its market, via `useMarket()` reading the tenant brand
 * out of React context. The problem was when it knew: `/api/brand` is a
 * network round trip, so the shell's first paint happened with the module
 * default, which was "uk". Hard-refreshing /crm/transactions on the US host
 * therefore painted a British product for one to two seconds — rail groups
 * reading "Win business / Sell & let", items reading "Appraisals",
 * "Viewings", "Lettings", and KPI cards reading £0 — before the American one
 * replaced it. An agent refreshing their own pipeline saw pounds sterling.
 *
 * A flash of the wrong currency is not a cosmetic bug. It is the moment a
 * paying customer wonders whether the software really is built for them, and
 * it happens on every refresh, not once.
 *
 * So the market is resolved here, before anything renders, from the first of
 * these that answers:
 *
 *   1. REACT_APP_MARKET, inlined at build time. `deploy-us.sh` builds the US
 *      box with REACT_APP_MARKET=us, so on that host this is decided before a
 *      single byte of JavaScript runs, which is the only way to be right on a
 *      first-ever visit with a cold cache.
 *   2. ?market= on the URL. What /crm-demo?market=us uses.
 *   3. The last market this browser actually resolved, from localStorage.
 *      Covers a build that was not told, from the second page load onward.
 *   4. "uk". Unchanged from before, so nothing about the UK product moves.
 *
 * The brand API stays the authority. This is a good guess made early; when
 * `/api/brand` answers, `rememberMarket()` records what it really was, and a
 * guess that turns out wrong is corrected on the same render as the tenant
 * data. The point is not to replace the lookup, it is to stop the gap before
 * the lookup being spent painting the wrong country.
 */

const VALID = ["uk", "us"];
const KEY = "fd_market";

/** "us" if the value is exactly that, else null. Never a guess. */
function clean(value) {
  const v = String(value || "").trim().toLowerCase();
  return VALID.includes(v) ? v : null;
}

function fromBuild() {
  // Read off `process.env` rather than destructured, because CRA does a
  // literal text substitution on `process.env.REACT_APP_*` at build time and
  // a destructured read is not substituted.
  try {
    return clean(process.env.REACT_APP_MARKET);
  } catch {
    return null;
  }
}

function fromQuery() {
  if (typeof window === "undefined") return null;
  try {
    return clean(new URLSearchParams(window.location.search).get("market"));
  } catch {
    return null;
  }
}

function fromStorage() {
  if (typeof window === "undefined") return null;
  try {
    return clean(window.localStorage.getItem(KEY));
  } catch {
    // Private browsing, or storage disabled. Not an error worth surfacing:
    // the next source in the chain is "uk", which is where we were anyway.
    return null;
  }
}

/**
 * The market to render with right now. Computed once per page load; it
 * cannot change under a running app without a navigation.
 *
 * @returns {"uk"|"us"}
 */
export const BOOT_MARKET = fromBuild() || fromQuery() || fromStorage() || "uk";

/**
 * The market the CRM is currently rendering, as opposed to the one it booted
 * with. Seeded from the boot guess and narrowed to the truth by CrmLayout
 * once `/api/brand` settles.
 *
 * This lives here, in a leaf module, rather than in CrmLayout where it used
 * to. CrmLayout imports CommandPalette, so anything in CommandPalette that
 * needed the market had to import back out of CrmLayout and close a cycle.
 * Cycles between ES modules resolve at call time rather than import time, so
 * it would have worked right up until somebody read one of those bindings at
 * module scope and got a temporal-dead-zone error with no obvious cause.
 * Nothing imports this file except to ask it a question, so there is no cycle
 * to close.
 */
let _active = BOOT_MARKET;

/** @returns {"uk"|"us"} */
export function activeMarket() {
  return _active;
}

/** Narrow the active market to a resolved value. Ignores anything else. */
export function setActiveMarket(m) {
  const v = clean(m);
  if (v) _active = v;
}

/**
 * Record the market the brand lookup actually returned, so the next load in
 * this browser starts from it rather than from "uk".
 *
 * Only ever called with a resolved value. Writing a guess here would make the
 * guess self-confirming and a genuinely wrong one permanent.
 *
 * @param {string} m
 */
export function rememberMarket(m) {
  const v = clean(m);
  if (!v || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(KEY, v);
  } catch {
    /* storage unavailable; the build flag and the query param still work */
  }
}
